import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Paginator } from 'primereact/paginator';
import { Message } from 'primereact/message';
import alertaService from '../../services/Alerta/AlertaService';
import { TagTipo, TagEvento, TagEstado, Codigo, Transportista, esCritico }
    from './alertaVisual';
import MapaAlerta from './MapaAlerta';
import { registrarLectura, nuevoLote } from './lecturaAlerta';
import HistorialAperturas from './HistorialAperturas';
import MapaViajeAlertas from './MapaViajeAlertas';
import { Semaforo, nivelDe, colorEvento } from './seguridadVisual';

/**
 * Consulta de alertas.
 *
 * A diferencia de "Mis alertas", que muestra lo que se le ENTREGO al usuario,
 * esta busca sobre todo lo que el usuario PUEDE VER segun sus suscripciones.
 * El alcance lo resuelve el backend: esta pantalla no lo decide ni lo envia.
 *
 * Lo que gobierna el diseno:
 *
 *  - VOLUMEN. Hay 4 millones de alertas desde 2023. El backend exige un rango
 *    de fechas o un codigo, y devuelve el motivo si faltan los dos. La
 *    pantalla arranca en HOY (fecha de Ecuador, no UTC).
 *
 *  - SE MIRA DESDE EL CELULAR. Una sola caja arriba filtra mientras se escribe
 *    ("597"), por pedazo, en contenedor, booking, candado, placa y buque. Los
 *    demas filtros viven en una hoja aparte.
 *
 *  - POR VIAJE. Por defecto se agrupa por viaje (placa + candado + booking +
 *    contenedor): una misma unidad genera decenas de alertas, y el guardia
 *    revisa la unidad. "Revisé estas N alertas" deja constancia de todas. La
 *    lista suelta sigue a un toque.
 *
 *  - AUDITORIA. Cada apertura se registra (fecha, veces, origen, IP, GPS).
 *
 * Los filtros se declaran en FILTROS: agregar uno es una entrada mas.
 */

/** true cuando la pantalla es angosta. Se recalcula si rotan el equipo. */
const useEsAngosto = (limite = 768) => {
    const consulta = `(max-width: ${limite}px)`;
    const [angosto, setAngosto] = useState(
        () => (typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia(consulta).matches : false)
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(consulta);
        const alCambiar = (e) => setAngosto(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', alCambiar);
        else mq.addListener(alCambiar);
        setAngosto(mq.matches);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', alCambiar);
            else mq.removeListener(alCambiar);
        };
    }, [consulta]);
    return angosto;
};

/** Los codigos identifican una unidad y por eso habilitan buscar sin fecha. */
const CODIGOS = ['placa', 'contenedor', 'booking', 'candado'];

const FILTROS = [
    { id: 'tipo', lbl: 'Tipo', clase: 'lista', catalogo: 'tipos' },
    { id: 'evento', lbl: 'Evento', clase: 'lista', catalogo: 'eventos' },
    { id: 'transportista', lbl: 'Transportista', clase: 'lista', catalogo: 'transportistas' },
    // Los ejemplos van con "ej." adelante: sin eso se leen como valores ya
    // cargados y el operador cree que la pantalla viene con un filtro puesto.
    { id: 'placa', lbl: 'Placa', clase: 'codigo', ph: 'ej. GSB1499' },
    { id: 'contenedor', lbl: 'Contenedor', clase: 'codigo', ph: 'ej. CAAU4107784' },
    { id: 'booking', lbl: 'Booking', clase: 'codigo', ph: 'ej. B40BK970686' },
    { id: 'candado', lbl: 'Candado', clase: 'codigo', ph: 'ej. DLC0921' }
];

const TAMANO = 50;

/** Ventana por defecto: las ultimas 24 horas hasta ahora. Se mueve sola. */
const HORAS_DEFECTO = 24;
const VENTANA = { horas: HORAS_DEFECTO };
/** Cada cuanto se refresca la ventana movil con la pantalla abierta. */
const REFRESCO_MS = 60000;
const TAMANO_VIAJES = 20;

/** Cuantas letras hacen falta para que la caja empiece a buscar. */
const MIN_TEXTO = 3;
/** Pausa despues de la ultima tecla antes de consultar. */
const PAUSA_MS = 450;

const MODOS = [
    { label: 'Por viaje', value: 'viaje', icon: 'pi pi-truck' },
    { label: 'Lista', value: 'lista', icon: 'pi pi-list' }
];

/**
 * Por que una alerta del rango no entra al tablero de seguridad, en palabras.
 * Las claves son las de UnidadesSeguridad.motivoExclusion en el backend.
 */
const MOTIVOS_EXCLUSION = {
    FINCA: ['en la finca', 'entre el fin del vacío y el inicio del lleno: son escenarios de la carga'],
    DESPUES: ['después del lleno', 'camino al puerto: ya no es tramo del viaje (se habilitará con el seguimiento a puerto)'],
    SIN_CICLO: ['de viajes sin VACÍO ni LLENO', 'su contenedor no tiene un viaje vacío o lleno registrado'],
    FUERA_DE_CICLO: ['antes del vacío', 'ocurrieron antes de que arrancara el viaje'],
    SIN_VIAJE: ['sin viaje', 'no pertenecen a ningún viaje (por ejemplo, el pánico de servicio VIP)']
};

const leerModo = () => {
    try { return window.localStorage.getItem('consultaAlertas.modo') || 'viaje'; } catch (e) { return 'viaje'; }
};
const guardarModo = (m) => {
    try { window.localStorage.setItem('consultaAlertas.modo', m); } catch (e) { /* preferencia, no dato */ }
};

/**
 * seguridad = true: la misma pantalla, leida en contexto de operacion de
 * seguridad. Cada viaje trae su semaforo (paradas en el mismo lugar, tarjetas
 * sin resolver) y los rojos van primero; el detalle del viaje abre el mapa con
 * el recorrido y cada alerta en su punto. Las reglas y sus umbrales viven en
 * el backend (ReglasSeguridad + alerta_contexto_parametro).
 */
const ConsultaAlertas = ({ seguridad = false }) => {

    const toast = useRef(null);
    const esAngosto = useEsAngosto();

    // Si se llega desde el tablero, la URL trae el filtro de la barra que se
    // toco. Sin esto habria que rehacerlo a mano y el tablero seria solo un
    // poster.
    const desdeUrl = () => {
        const h = window.location.hash || '';
        const i = h.indexOf('?');
        if (i < 0) return null;
        const p = new URLSearchParams(h.slice(i + 1));
        const f = {};
        ['desde', 'hasta', 'tipo', 'evento', 'transportista',
            'placa', 'contenedor', 'booking', 'candado'].forEach((k) => {
                const v = p.get(k);
                if (v) f[k] = v;
            });
        return Object.keys(f).length ? f : null;
    };

    // Arranca en las ULTIMAS 24 HORAS, no en "hoy": el dia calendario se
    // quedaba fijo al pasar la medianoche y partia en dos los viajes que la
    // cruzan. La ventana la calcula el servidor en hora de Ecuador, en cada
    // consulta, asi que avanza sola.
    const [filtros, setFiltros] = useState(desdeUrl() || { ...VENTANA });
    const [vinoDelTablero, setVinoDelTablero] = useState(desdeUrl() != null);

    const [texto, setTexto] = useState('');
    const [modo, setModo] = useState(seguridad ? 'viaje' : leerModo);
    const [parametros, setParametros] = useState(null);
    const [criterios, setCriterios] = useState(null);
    const [excluidas, setExcluidas] = useState(null);
    const [verExcluidas, setVerExcluidas] = useState(false);
    const [verCriterios, setVerCriterios] = useState(false);
    const [seg, setSeg] = useState(null);
    const [ocultarOperacion, setOcultarOperacion] = useState(false);

    // Filtros de la grilla. Afinan LO QUE YA ESTA EN PANTALLA.
    const [filtrosCol, setFiltrosCol] = useState({
        tipoEvento: { value: null, matchMode: FilterMatchMode.CONTAINS },
        novedad: { value: null, matchMode: FilterMatchMode.CONTAINS },
        placa: { value: null, matchMode: FilterMatchMode.CONTAINS },
        dispositivo: { value: null, matchMode: FilterMatchMode.CONTAINS },
        booking: { value: null, matchMode: FilterMatchMode.CONTAINS },
        contenedor: { value: null, matchMode: FilterMatchMode.CONTAINS },
        transportista: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [catalogos, setCatalogos] = useState({ tipos: [], eventos: [], transportistas: [], alcance: [] });
    const [datos, setDatos] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalAlertas, setTotalAlertas] = useState(0);
    const [pagina, setPagina] = useState(0);
    const [cargando, setCargando] = useState(false);
    const [aviso, setAviso] = useState(null);
    const [abrirFiltros, setAbrirFiltros] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [viaje, setViaje] = useState(null);
    const [alertasViaje, setAlertasViaje] = useState([]);
    const [cargandoViaje, setCargandoViaje] = useState(false);
    const [revisando, setRevisando] = useState(false);

    useEffect(() => {
        alertaService.consultaCatalogos()
            .then((c) => setCatalogos(c || {}))
            .catch(() => { /* la consulta funciona igual sin los desplegables */ });
    }, []);

    // La caja se pasa a los filtros recien cuando el usuario deja de tipear, y
    // desde 3 caracteres: "5" solo traeria medio dia.
    useEffect(() => {
        const t = setTimeout(() => {
            const limpio = texto.trim();
            const q = limpio.length >= MIN_TEXTO ? limpio : '';
            setFiltros((p) => ((p.q || '') === q ? p : { ...p, q }));
            setPagina(0);
        }, PAUSA_MS);
        return () => clearTimeout(t);
    }, [texto]);

    // Cada consulta lleva un numero: si llega la respuesta de una vieja despues
    // de una nueva (tipeo rapido), se descarta.
    const turno = useRef(0);

    const buscar = useCallback(async (f, pag, m, silencioso = false) => {
        const mio = ++turno.current;
        if (!silencioso) {
            setCargando(true);
            setAviso(null);
        }
        try {
            const r = m === 'viaje'
                ? await (seguridad
                    ? alertaService.consultarSeguridad(f, pag, TAMANO_VIAJES)
                    : alertaService.consultarPorViaje(f, pag, TAMANO_VIAJES))
                : await alertaService.consultar(f, pag, TAMANO);
            if (seguridad && r?.parametros) setParametros(r.parametros);
            if (seguridad && Array.isArray(r?.criterios)) setCriterios(r.criterios);
            if (seguridad) setExcluidas(r?.excluidasTotal ? { total: r.excluidasTotal, porMotivo: r.excluidas || {} } : null);
            if (mio !== turno.current) return;
            if (r?.ok === false) {
                // No es un error: es el control de volumen del backend.
                setAviso(r.mensaje);
                setDatos([]);
                setGrupos([]);
                setTotal(0);
                setTotalAlertas(0);
            } else if (m === 'viaje') {
                setGrupos(Array.isArray(r?.grupos) ? r.grupos : []);
                setTotal(r?.totalGrupos || 0);
                setTotalAlertas(r?.totalAlertas || 0);
                if (r?.recortado) {
                    setAviso('Hay más alertas de las que se pueden agrupar de una vez: se agruparon las 20.000 más recientes. Achicá el rango para ver el resto.');
                }
            } else {
                setDatos(Array.isArray(r?.datos) ? r.datos : []);
                setTotal(r?.total || 0);
            }
        } catch (e) {
            if (mio !== turno.current) return;
            setDatos([]);
            setGrupos([]);
            setTotal(0);
            setTotalAlertas(0);
            toast.current?.show({
                severity: 'error', summary: 'No se pudo consultar',
                detail: 'Reintentá en unos segundos.', life: 4000
            });
        } finally {
            if (mio === turno.current) setCargando(false);
        }
    }, [seguridad]);

    useEffect(() => { buscar(filtros, pagina, modo); }, [buscar, filtros, pagina, modo]);

    // Con la ventana movil la pantalla se refresca sola cada minuto: lo que
    // entra despues de medianoche aparece sin tocar nada. Con fechas fijas no.
    useEffect(() => {
        if (!filtros.horas) return undefined;
        const t = setInterval(() => buscar(filtros, pagina, modo, true), REFRESCO_MS);
        return () => clearInterval(t);
    }, [buscar, filtros, pagina, modo]);

    const activos = useMemo(() => FILTROS
        .filter((x) => (filtros[x.id] || '').toString().trim() !== '')
        .map((x) => ({ id: x.id, lbl: x.lbl, val: filtros[x.id] })), [filtros]);

    const hayCodigo = useMemo(
        () => CODIGOS.some((c) => (filtros[c] || '').toString().trim() !== ''), [filtros]);

    const cambiar = (id, valor) => {
        setPagina(0);
        setFiltros((p) => {
            const n = { ...p, [id]: valor };
            // Elegir una fecha a mano deja la ventana movil: el rango queda fijo.
            if (id === 'desde' || id === 'hasta') delete n.horas;
            return n;
        });
    };

    const quitar = (id) => cambiar(id, '');

    const limpiar = () => {
        setPagina(0);
        setTexto('');
        setFiltros({ ...VENTANA });
    };

    /** Abre toda la historia. Solo tiene sentido con un codigo cargado. */
    const abrirHistoria = () => {
        setPagina(0);
        setFiltros((p) => {
            const n = { ...p, desde: '', hasta: '' };
            delete n.horas;
            return n;
        });
    };

    const volverAVentana = () => {
        setPagina(0);
        setFiltros((p) => ({ ...p, desde: '', hasta: '', ...VENTANA }));
    };

    const cambiarModo = (m) => {
        if (!m) return;
        guardarModo(m);
        setPagina(0);
        setModo(m);
    };

    const historico = !filtros.desde && !filtros.hasta && !filtros.horas;

    /** El rango en palabras, para la etiqueta. */
    const rangoTexto = filtros.horas
        ? `Últimas ${filtros.horas} h`
        : historico ? 'Toda la historia' : `${filtros.desde || '…'} a ${filtros.hasta || '…'}`;

    /** true si la alerta ingreso fuera del rango elegido (sirve en el detalle del viaje). */
    const fueraDelRango = (a) => {
        const v = a.fechaIngreso ? new Date(a.fechaIngreso) : null;
        if (!v || isNaN(v.getTime())) return false;
        if (filtros.horas) return v.getTime() < Date.now() - filtros.horas * 3600000;
        if (filtros.desde && v < new Date(`${filtros.desde}T00:00:00`)) return true;
        if (filtros.hasta && v >= new Date(new Date(`${filtros.hasta}T00:00:00`).getTime() + 86400000)) return true;
        return false;
    };

    const fecha = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toLocaleString('es-EC');
    };

    const hora = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
    };

    /** Hora sola si es de hoy; dia y hora si es de otro dia (un viaje cruza la medianoche). */
    const cuando = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d.getTime())) return '';
        const hoy = new Date();
        const mismoDia = d.toDateString() === hoy.toDateString();
        return mismoDia ? hora(v)
            : `${d.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit' })} ${hora(v)}`;
    };

    const opciones = (lista) => (lista || []).map((v) => ({ label: v, value: v }));

    // ---------------------------------------------------------------- lectura

    /**
     * Abre el detalle de una alerta y registra la apertura. Lo que muestra de
     * "tu lectura" es lo que devolvio el servidor.
     */
    const abrirDetalle = (a) => {
        setDetalle(a);
        registrarLectura([a.monitoreoDetId], 'CONSULTA').then((por) => {
            const x = (por && por[String(a.monitoreoDetId)]) || {};
            setDetalle((d) => (d && d.monitoreoDetId === a.monitoreoDetId
                ? { ...d, lecturaLista: true, fechaLeida: x.fechaLeida,
                    vistas: x.vistas, ultimaVista: x.ultimaVista } : d));
        });
    };

    /** Abre un viaje: trae TODAS sus alertas dentro del mismo filtro. */
    const abrirViaje = async (g) => {
        setViaje(g);
        setAlertasViaje([]);
        setSeg(null);
        // Abrir la lista del viaje tambien queda registrado, como hecho aparte:
        // no marca alertas leidas ni cuenta como "vio" o "reviso".
        if (g.viajeId != null && g.monitoreoDetIds?.length) {
            registrarLectura([g.monitoreoDetIds[0]], 'VIAJE_ABIERTO');
        }
        if (g.viajeId == null || g.vip) {
            // Alerta sin viaje: el grupo es ella sola. El panico VIP tampoco tiene
            // viaje (su origen es la posicion de Traccar): sus datos ya vienen en g.vip.
            setAlertasViaje([]);
            return;
        }
        setCargandoViaje(true);
        try {
            // El viaje COMPLETO, sin el rango ni los demas filtros: un viaje que
            // empezo ayer y sigue hoy se ve entero. Las de fuera del rango se marcan.
            if (seguridad) {
                const r = await alertaService.viajeSeguridad(g.viajeId);
                if (r?.ok === false) {
                    toast.current?.show({ severity: 'warn', summary: r.mensaje, life: 4000 });
                } else {
                    setSeg(r);
                    setAlertasViaje(Array.isArray(r?.eventos) ? r.eventos : []);
                }
            } else {
                const r = await alertaService.consultar({ viajeId: g.viajeId }, 0, 100);
                setAlertasViaje(Array.isArray(r?.datos) ? r.datos : []);
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'No se pudieron traer las alertas del viaje', life: 4000 });
        } finally {
            setCargandoViaje(false);
        }
    };

    /**
     * "Revisé estas N alertas": una fila de auditoria por alerta, todas con el
     * mismo lote, y las marca leidas. Es la evidencia de que se reviso la unidad.
     */
    const revisarViaje = async () => {
        if (!viaje) return;
        const ids = viaje.monitoreoDetIds || [];
        if (!ids.length) return;
        setRevisando(true);
        const por = await registrarLectura(ids, 'VIAJE', { lote: nuevoLote() });
        setRevisando(false);
        if (por) {
            toast.current?.show({
                severity: 'success', summary: 'Revisión registrada',
                detail: `${ids.length} ${ids.length === 1 ? 'alerta' : 'alertas'} · ${new Date().toLocaleTimeString('es-EC')}`,
                life: 3500
            });
            setViaje(null);
            buscar(filtros, pagina, modo);
        } else {
            toast.current?.show({
                severity: 'warn', summary: 'No se pudo registrar la revisión',
                detail: 'Revisá la conexión y volvé a intentar.', life: 5000
            });
        }
    };

    // ---------------------------------------------------------------- piezas

    /** Estado de revision del viaje para este usuario. */
    const EstadoRevision = ({ g }) => {
        if (g.nuevasDesdeRevision > 0) {
            return <Tag severity="warning" value={`${g.nuevasDesdeRevision} nuevas desde tu revisión`} />;
        }
        if (g.sinVer === 0) {
            return <Tag severity="success" icon="pi pi-check"
                        value={g.ultimaRevision ? `Revisado ${hora(g.ultimaRevision)}` : 'Todas vistas'} />;
        }
        if (g.sinVer > 0) {
            return <Tag severity="info" value={`${g.sinVer} sin ver`} />;
        }
        return null;
    };

    /**
     * Lo que el operador revisa de un panico de servicio VIP: no tiene placa,
     * candado ni contenedor, tiene un dispositivo. "Permiso hasta" es hasta
     * cuando el operador puede ver el dispositivo en Traccar.
     */
    const DatosVip = ({ vip }) => {
        const vigente = vip.permisoHasta && new Date(vip.permisoHasta).getTime() > Date.now();
        return (
            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="text-xs text-color-secondary uppercase">Dispositivo VIP</div>
                    <div className="font-medium">{vip.dispositivo || '—'}</div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="text-xs text-color-secondary uppercase">Identificador</div>
                    <div className="font-medium"><Codigo valor={vip.identificador} /></div>
                </div>
                <div className="col-6">
                    <div className="text-xs text-color-secondary uppercase">Pánico</div>
                    <div className="font-medium">{fecha(vip.fechaPanico) || '—'}</div>
                </div>
                <div className="col-6">
                    <div className="text-xs text-color-secondary uppercase">Permiso en Traccar hasta</div>
                    <div className="font-medium">
                        {fecha(vip.permisoHasta) || '—'}
                        {vip.permisoHasta && (
                            <Tag className="ml-2" severity={vigente ? 'success' : 'secondary'}
                                 value={vigente ? 'vigente' : 'vencido'} />
                        )}
                    </div>
                </div>
                <div className="col-12">
                    <div className="text-xs text-color-secondary uppercase">Observación</div>
                    <div>{vip.observacion || '—'}</div>
                </div>
                <div className="col-12">
                    <div className="text-xs text-color-secondary uppercase">Ubicación del pánico</div>
                    {vip.mapaUrl ? (
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <Codigo valor={`${vip.latitud}, ${vip.longitud}`} />
                            <a href={vip.mapaUrl} target="_blank" rel="noopener noreferrer"
                               className="p-button p-button-sm p-button-outlined no-underline">
                                <i className="pi pi-map-marker mr-2" />Ver en mapa
                            </a>
                        </div>
                    ) : (
                        <div className="text-color-secondary">El pánico llegó sin posición GPS.</div>
                    )}
                </div>
            </div>
        );
    };

    /** El contenedor en su ciclo VACIO -> LLENO: que tramos tiene y cuantas alertas en cada uno. */
    const CicloContenedor = ({ ciclo, porTramo }) => {
        if (!ciclo) return null;
        const tramo = (nombre, ini, fin) => ini && (
            <span className="inline-flex align-items-center gap-1 mr-3">
                <Tag value={nombre} style={{ background: nombre === 'Lleno' ? '#185FA5' : '#7F77DD' }} />
                <span className="text-xs text-color-secondary">
                    {cuando(ini)} – {fin ? cuando(fin) : 'en curso'}
                    {porTramo && porTramo[nombre] ? ` · ${porTramo[nombre]}` : ''}
                </span>
            </span>
        );
        return (
            <div className="mt-2 flex flex-wrap align-items-center">
                {tramo('Vacío', ciclo.vacioInicio, ciclo.vacioFin)}
                {tramo('Lleno', ciclo.llenoInicio, ciclo.llenoFin)}
            </div>
        );
    };

    const tarjetaViaje = (g) => {
        const tipos = Object.entries(g.porTipo || {});
        return (
            <button key={g.viajeId ?? `a${g.monitoreoDetIds?.[0]}`} type="button" onClick={() => abrirViaje(g)}
                    className="w-full h-full text-left p-3 border-1 border-round surface-card cursor-pointer"
                    style={{ borderColor: 'var(--surface-border)',
                             borderLeft: seguridad ? `5px solid ${nivelDe(g.nivel).color}` : undefined }}>
                {seguridad && (
                    <div className="mb-2">
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <Semaforo nivel={g.nivel} />
                            <span className="text-sm text-color-secondary">
                                {g.alertasReales ?? 0} {g.alertasReales === 1 ? 'alerta' : 'alertas'}
                                {g.operaciones ? ` · ${g.operaciones} de operación` : ''}
                            </span>
                        </div>
                        {(g.motivos || []).map((m) => (
                            <div key={m} className="text-sm mt-1" style={{ color: nivelDe(g.nivel).texto }}>{m}</div>
                        ))}
                        <CicloContenedor ciclo={g.ciclo} porTramo={g.porTramo} />
                    </div>
                )}
                <div className="flex align-items-center gap-2 flex-wrap mb-2">
                    {tipos.map(([t, n]) => (
                        <span key={t} className="flex align-items-center gap-1">
                            <TagTipo tipo={t} /><b>{n}</b>
                        </span>
                    ))}
                    <span className="ml-auto text-sm text-color-secondary text-right">
                        <span className="block text-xs uppercase" style={{ opacity: .75 }}>Última</span>
                        {cuando(g.ultima)}
                    </span>
                </div>
                <div className="font-semibold mb-2">
                    {g.vip
                        ? <span>Pánico VIP · {g.vip.dispositivo || g.vip.identificador || '—'}</span>
                        : <Transportista nombre={g.transportista} origen={g.transportistaOrigen} />}
                </div>
                <div className="flex flex-wrap gap-3 mb-2">
                    {(g.vip ? [['Identificador', g.vip.identificador]]
                        : [['Placa', g.placa], ['Candado', g.candado], ['Contenedor', g.contenedor],
                           ['Booking', g.booking], ['Buque', g.buque]])
                        .filter(([, v]) => v)
                        .map(([k, v]) => (
                            <div key={k}>
                                <div className="text-xs text-color-secondary uppercase">{k}</div>
                                <div className="font-medium"><Codigo valor={v} /></div>
                            </div>
                        ))}
                </div>
                <div className="flex align-items-center gap-2 flex-wrap text-sm text-color-secondary">
                    <span>
                        {g.totalViaje && g.totalViaje !== g.total
                            ? `${g.total} en el rango · ${g.totalViaje} del viaje`
                            : `${g.total} ${g.total === 1 ? 'alerta' : 'alertas'}`}
                        {' · '}{hora(g.primera)} a {hora(g.ultima)}
                    </span>
                    <span className="ml-auto"><EstadoRevision g={g} /></span>
                </div>
            </button>
        );
    };

    const tarjeta = (a) => {
        const grave = esCritico(a.novedad) || esCritico(a.evento);
        return (
            <button key={a.monitoreoDetId} type="button" onClick={() => abrirDetalle(a)}
                    className="w-full text-left p-3 border-1 border-round surface-card cursor-pointer"
                    style={{
                        borderColor: 'var(--surface-border)',
                        borderLeft: `3px solid ${grave ? 'var(--red-500)' : 'var(--surface-border)'}`
                    }}>
                <div className="flex align-items-center gap-2 flex-wrap mb-2">
                    <TagTipo tipo={a.tipoEvento} />
                    {/* Con la etiqueta a la vista: en el celular no hay hover, y
                        entre que el evento ocurre y entra a CEMA pasan 37 minutos
                        en promedio. */}
                    <span className="ml-auto text-sm text-color-secondary text-right"
                          title="Cuándo ocurrió el evento, no cuándo llegó la alerta">
                        <span className="block text-xs uppercase" style={{ opacity: .75 }}>
                            Evento
                        </span>
                        {fecha(a.fechaEvento)}
                    </span>
                </div>
                <div className="font-semibold mb-2">
                    {a.vip
                        ? <span>Pánico VIP · {a.vip.dispositivo || '—'}</span>
                        : <Transportista nombre={a.transportista} origen={a.transportistaOrigen} />}
                </div>
                <div className="flex flex-wrap gap-3 mb-2">
                    {(a.vip ? [['Identificador', a.vip.identificador]]
                        : [['Placa', a.placa], ['Contenedor', a.contenedor], ['Booking', a.booking]])
                        .map(([k, v]) => (
                            <div key={k}>
                                <div className="text-xs text-color-secondary uppercase">{k}</div>
                                <div className="font-medium"><Codigo valor={v} /></div>
                            </div>
                        ))}
                </div>
                <div className="text-sm p-2 border-round surface-100">
                    {(a.novedad || a.observacion)
                        ? <span>
                            {a.novedad && <span className="mr-2"><TagEvento evento={a.novedad} /></span>}
                            {a.observacion}
                          </span>
                        : <span className="text-color-secondary">Sin respuesta del operador</span>}
                </div>
            </button>
        );
    };

    const panelFiltros = (
        <div>
            <div className="mb-4">
                <div className="text-sm font-bold mb-2">Rango de fechas</div>
                <div className="flex gap-2 flex-wrap">
                    <span className="flex-1" style={{ minWidth: '9rem' }}>
                        <label htmlFor="fDesde" className="block text-xs text-color-secondary mb-1">Desde</label>
                        <input id="fDesde" type="date" className="p-inputtext w-full"
                               value={filtros.desde || ''}
                               onChange={(e) => cambiar('desde', e.target.value)} />
                    </span>
                    <span className="flex-1" style={{ minWidth: '9rem' }}>
                        <label htmlFor="fHasta" className="block text-xs text-color-secondary mb-1">Hasta</label>
                        <input id="fHasta" type="date" className="p-inputtext w-full"
                               value={filtros.hasta || ''}
                               onChange={(e) => cambiar('hasta', e.target.value)} />
                    </span>
                </div>
                <div className="mt-2">
                    {!filtros.horas && (
                        <Button label={`Últimas ${HORAS_DEFECTO} h`} icon="pi pi-undo"
                                className="p-button-sm p-button-text" onClick={volverAVentana} />
                    )}
                    {!historico && (
                        <Button label="Buscar en toda la historia" icon="pi pi-history"
                                className="p-button-sm p-button-text" disabled={!hayCodigo}
                                onClick={abrirHistoria} />
                    )}
                    {filtros.horas && (
                        <div className="text-xs text-color-secondary mt-1">
                            Mostrando las últimas {filtros.horas} horas hasta ahora; se actualiza sola cada minuto.
                            Elegí fechas para fijar un rango.
                        </div>
                    )}
                    {!hayCodigo && !historico && (
                        <div className="text-xs text-color-secondary mt-1">
                            Para abrir toda la historia cargá antes un código completo: placa,
                            contenedor, booking o candado.
                        </div>
                    )}
                </div>
            </div>

            {FILTROS.map((x) => (
                <div key={x.id} className="mb-3">
                    <label htmlFor={`f_${x.id}`} className="block text-xs text-color-secondary mb-1">
                        {x.lbl}
                    </label>
                    {x.clase === 'lista' ? (
                        <Dropdown inputId={`f_${x.id}`} className="w-full" showClear filter
                                  value={filtros[x.id] || null}
                                  options={opciones(catalogos[x.catalogo])}
                                  onChange={(e) => cambiar(x.id, e.value || '')}
                                  placeholder="Todos" />
                    ) : (
                        <InputText id={`f_${x.id}`} className="w-full" value={filtros[x.id] || ''}
                                   placeholder={x.ph} autoComplete="off"
                                   onChange={(e) => cambiar(x.id, e.target.value)} />
                    )}
                </div>
            ))}

            {/* Aca los codigos se buscan completos a proposito: habilitan toda la
                historia, y por pedazo sin fecha no hay indice que aguante. Por
                pedazo esta la caja de arriba, dentro del rango. */}
            <small className="text-color-secondary">
                Acá los códigos se buscan completos. Para buscar por pedazo usá la caja de arriba.
            </small>
        </div>
    );

    const alcance = catalogos.alcance || [];
    const textoCorto = texto.trim().length > 0 && texto.trim().length < MIN_TEXTO;

    return (
        <div className="grid">
            <Toast ref={toast} />

            <div className="col-12">
                <div className="card">
                    <div className="flex flex-wrap justify-content-between align-items-center gap-3">
                        <span className="text-xl font-bold">
                            {seguridad ? 'Alertas · Operación de seguridad' : 'Consulta de alertas'}
                        </span>
                        <div className="flex align-items-center gap-2">
                            {seguridad && criterios && (
                                <Button label="¿Cómo se calcula?" icon="pi pi-question-circle"
                                        className="p-button-text" onClick={() => setVerCriterios(true)} />
                            )}
                            <Button label="Filtros" icon="pi pi-filter"
                                    badge={activos.length ? String(activos.length) : null}
                                    onClick={() => setAbrirFiltros(true)} />
                            <Button icon="pi pi-refresh" className="p-button-text"
                                    tooltip="Actualizar" onClick={() => buscar(filtros, pagina, modo)} />
                        </div>
                    </div>

                    {/* La caja unica. Filtra sola al dejar de tipear: no hay boton. */}
                    <div className="mt-3">
                        <span className="p-input-icon-left p-input-icon-right w-full">
                            <i className="pi pi-search" />
                            <InputText id="buscaRapida" value={texto} className="w-full"
                                       inputMode="search" autoComplete="off"
                                       onChange={(e) => setTexto(e.target.value)}
                                       placeholder="Contenedor, booking, candado, placa o buque · ej. 597" />
                            {texto && (
                                <i className="pi pi-times cursor-pointer" role="button" aria-label="Borrar búsqueda"
                                   onClick={() => setTexto('')} />
                            )}
                        </span>
                        {textoCorto && (
                            <small className="text-color-secondary">
                                Escribí al menos {MIN_TEXTO} caracteres.
                            </small>
                        )}
                    </div>

                    <div className="flex flex-wrap align-items-center gap-2 mt-3">
                        {!seguridad && (
                            <SelectButton value={modo} options={MODOS} onChange={(e) => cambiarModo(e.value)}
                                          itemTemplate={(o) => <span><i className={`${o.icon} mr-2`} />{o.label}</span>} />
                        )}
                        <Tag icon={filtros.horas ? 'pi pi-clock' : 'pi pi-calendar'} severity="info"
                             className="cursor-pointer" onClick={() => setAbrirFiltros(true)}
                             value={rangoTexto} />
                        {activos.map((a) => (
                            <Tag key={a.id} className="cursor-pointer"
                                 onClick={() => quitar(a.id)}
                                 value={`${a.lbl}: ${a.val}  ✕`} />
                        ))}
                        {(activos.length > 0 || texto) && (
                            <Button label="Limpiar" className="p-button-text p-button-sm"
                                    onClick={limpiar} />
                        )}
                    </div>

                    {vinoDelTablero && (
                        <div className="flex align-items-center gap-2 mt-2 text-sm"
                             style={{ color: 'var(--primary-color)' }}>
                            <i className="pi pi-chart-bar" style={{ fontSize: '.8rem' }} />
                            <span>Filtros traídos del tablero. Cambialos o quitalos acá si querés.</span>
                            <Button label="Volver al tablero" className="p-button-text p-button-sm"
                                    icon="pi pi-arrow-left"
                                    onClick={() => { window.location.hash = '#/tablero-alertas'; }} />
                            <Button icon="pi pi-times" className="p-button-text p-button-sm"
                                    tooltip="Ocultar este aviso"
                                    onClick={() => setVinoDelTablero(false)} />
                        </div>
                    )}

                    {alcance.length > 0 && (
                        <div className="mt-2 text-sm text-color-secondary">
                            Ves {alcance.map((x) => `${x.tipo} de ${x.empresa}`).join(' · ')}.
                        </div>
                    )}

                    {seguridad && (
                        <div className="mt-2 text-sm text-color-secondary flex align-items-center gap-2 flex-wrap">
                            <Semaforo nivel={2} /> <Semaforo nivel={1} /> <Semaforo nivel={0} />
                            <span>Los viajes en Crítico van primero. Lo que es operación normal se muestra en gris.</span>
                        </div>
                    )}

                    {seguridad && excluidas && (
                        <div className="mt-2 p-2 border-round surface-100 text-sm">
                            <span>
                                <i className="pi pi-filter-slash mr-2 text-color-secondary" />
                                {excluidas.total} {excluidas.total === 1 ? 'alerta del rango no entra' : 'alertas del rango no entran'} en
                                este tablero, que sigue solo el viaje del contenedor (vacío y lleno).
                            </span>
                            <Button label={verExcluidas ? 'Ocultar' : '¿Cuáles y por qué?'} className="p-button-text p-button-sm"
                                    onClick={() => setVerExcluidas(!verExcluidas)} />
                            <a href="#/consulta-alertas" className="ml-1 font-semibold" style={{ color: 'var(--primary-color)' }}>
                                Ver todas en Consulta de alertas <i className="pi pi-arrow-right" style={{ fontSize: '.75rem' }} />
                            </a>
                            {verExcluidas && (
                                <ul className="mt-2 mb-1 pl-4">
                                    {Object.entries(excluidas.porMotivo).map(([k, n]) => (
                                        <li key={k} className="mb-1">
                                            <b>{n}</b> {(MOTIVOS_EXCLUSION[k] || [k])[0]}
                                            {MOTIVOS_EXCLUSION[k] && (
                                                <span className="text-color-secondary">: {MOTIVOS_EXCLUSION[k][1]}.</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {aviso && <div className="mt-3"><Message severity="warn" text={aviso} /></div>}

                    <div className="mt-3 text-sm text-color-secondary">
                        {cargando ? 'Buscando…'
                            : modo === 'viaje'
                                ? `${total} ${total === 1 ? 'viaje' : 'viajes'} · ${totalAlertas} ${totalAlertas === 1 ? 'alerta' : 'alertas'}`
                                : `${total} ${total === 1 ? 'alerta' : 'alertas'}`}
                        {modo === 'lista' && !esAngosto && !cargando && datos.length > 0 && (
                            <span className="ml-2">
                                · los filtros de la grilla afinan las {datos.length} de esta página
                            </span>
                        )}
                    </div>

                    {modo === 'viaje' ? (
                        <div className="grid mt-1">
                            {!cargando && grupos.length === 0 && !aviso &&
                                <div className="col-12 text-color-secondary">Sin resultados.</div>}
                            {grupos.map((g) => (
                                <div key={g.viajeId ?? `a${g.monitoreoDetIds?.[0]}`} className="col-12 md:col-6 xl:col-4">
                                    {tarjetaViaje(g)}
                                </div>
                            ))}
                        </div>
                    ) : esAngosto ? (
                        <div className="flex flex-column gap-2 mt-2">
                            {!cargando && datos.length === 0 && !aviso &&
                                <div className="text-color-secondary">Sin resultados.</div>}
                            {datos.map(tarjeta)}
                        </div>
                    ) : (
                        <DataTable value={datos} loading={cargando} className="mt-2"
                                   emptyMessage="Sin resultados." responsiveLayout="scroll"
                                   dataKey="monitoreoDetId"
                                   onRowClick={(e) => abrirDetalle(e.data)}
                                   rowClassName={(a) => (esCritico(a.novedad) ? 'bg-red-50' : '')}
                                   filterDisplay="row" filters={filtrosCol}
                                   onFilter={(e) => setFiltrosCol(e.filters)}
                                   rowHover>
                            {/* Es cuando ocurrio el evento, no cuando llego la alerta:
                                entre las dos cosas pasan 37 minutos en promedio. */}
                            <Column field="fechaEvento" header="Fecha del evento"
                                    body={(a) => fecha(a.fechaEvento)} style={{ minWidth: '11rem' }} />
                            <Column header="Tipo" field="tipoEvento" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => <TagTipo tipo={a.tipoEvento} />}
                                    style={{ minWidth: '9rem' }} />
                            <Column header="Evento" field="novedad" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => <TagEvento evento={a.novedad} />}
                                    style={{ minWidth: '15rem' }} />
                            <Column header="Placa" field="placa" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => (a.vip ? null : <Codigo valor={a.placa} />)}
                                    style={{ minWidth: '8rem' }} />
                            <Column header="Candado" field="dispositivo" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => (a.vip ? null : <Codigo valor={a.dispositivo} />)}
                                    style={{ minWidth: '9rem' }} />
                            <Column header="Booking" field="booking" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => (a.vip ? null : <Codigo valor={a.booking} />)}
                                    style={{ minWidth: '11rem' }} />
                            <Column header="Contenedor" field="contenedor" filter showFilterMenu={false}
                                    filterPlaceholder="Filtrar"
                                    body={(a) => (a.vip ? null : <Codigo valor={a.contenedor} />)}
                                    style={{ minWidth: '11rem' }} />
                            <Column header="Buque" field="buque" style={{ minWidth: '10rem' }} />
                            <Column header="Transportista" field="transportista"
                                    filter showFilterMenu={false} filterPlaceholder="Filtrar"
                                    style={{ minWidth: '11rem' }}
                                    body={(a) => (a.vip
                                        ? <span><Tag value="VIP" className="mr-2" />{a.vip.dispositivo || a.vip.identificador}</span>
                                        : <Transportista nombre={a.transportista}
                                                         origen={a.transportistaOrigen} />)} />
                            <Column field="observacion" header="Respuesta del operador"
                                    style={{ minWidth: '14rem' }} />
                        </DataTable>
                    )}

                    {total > (modo === 'viaje' ? TAMANO_VIAJES : TAMANO) && (
                        <Paginator first={pagina * (modo === 'viaje' ? TAMANO_VIAJES : TAMANO)}
                                   rows={modo === 'viaje' ? TAMANO_VIAJES : TAMANO} totalRecords={total}
                                   onPageChange={(e) => setPagina(e.page)} className="mt-2" />
                    )}
                </div>
            </div>

            {seguridad && criterios && (
                <Dialog visible={verCriterios} onHide={() => setVerCriterios(false)} header="¿Cómo se calcula el semáforo?"
                        style={{ width: '46rem', maxWidth: '96vw' }} dismissableMask>
                    <div className="flex gap-2 flex-wrap mb-3">
                        <Semaforo nivel={2} /> <span className="text-sm">pide acción</span>
                        <Semaforo nivel={1} /> <span className="text-sm">conviene mirar</span>
                        <Semaforo nivel={0} /> <span className="text-sm">nada que revisar</span>
                    </div>
                    {criterios.map((c) => (
                        <div key={c.regla} className="mb-3 pb-3 border-bottom-1" style={{ borderColor: 'var(--surface-border)' }}>
                            <div className="font-semibold mb-1">{c.regla}</div>
                            <div className="mb-1">{c.detalle}</div>
                            <div className="text-sm text-color-secondary mb-1">Por qué: {c.porque}</div>
                            {(c.parametros || []).length > 0 && (
                                <div className="text-xs text-color-secondary">
                                    Parámetros: {c.parametros.map((k) => (
                                        <code key={k} className="mr-2">{k} = {parametros?.[k]}</code>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <small className="text-color-secondary">
                        Los valores se cambian en la tabla alerta_contexto_parametro y rigen en la siguiente consulta.
                        El detalle de cada criterio está en «Criterios del semáforo - Alertas Operacion de Seguridad».
                    </small>
                </Dialog>
            )}

            <Dialog visible={abrirFiltros} onHide={() => setAbrirFiltros(false)} header="Filtros"
                    style={{ width: '32rem', maxWidth: '96vw' }} dismissableMask
                    footer={
                        <div className="flex gap-2 justify-content-end">
                            <Button label="Limpiar" className="p-button-text" onClick={limpiar} />
                            <Button label="Ver resultados" icon="pi pi-check"
                                    onClick={() => setAbrirFiltros(false)} />
                        </div>
                    }>
                {panelFiltros}
            </Dialog>

            {/* ---- Un viaje: todas sus alertas y la revision ---- */}
            <Dialog visible={!!viaje} onHide={() => setViaje(null)}
                    header={viaje?.vip ? 'Pánico de servicio VIP' : 'Alertas del viaje'}
                    style={{ width: seguridad ? '64rem' : '46rem', maxWidth: '96vw' }} dismissableMask
                    footer={viaje && (
                        <div className="flex align-items-center gap-2 justify-content-end flex-wrap">
                            {viaje.ultimaRevision && (
                                <span className="text-sm text-color-secondary mr-auto">
                                    Tu última revisión: {fecha(viaje.ultimaRevision)}
                                </span>
                            )}
                            <Button label={`Revisé estas ${(viaje.monitoreoDetIds || []).length} ${(viaje.monitoreoDetIds || []).length === 1 ? 'alerta' : 'alertas'}`}
                                    icon="pi pi-check" className="p-button-success"
                                    loading={revisando} onClick={revisarViaje} />
                        </div>
                    )}>
                {viaje && (
                    <div>
                        {viaje.vip ? (
                            <div className="pb-2 mb-2 border-bottom-1" style={{ borderColor: 'var(--surface-border)' }}>
                                <DatosVip vip={viaje.vip} />
                            </div>
                        ) : (
                        <div className="grid pb-2 mb-2 border-bottom-1" style={{ borderColor: 'var(--surface-border)' }}>
                            {[['Placa', viaje.placa], ['Candado', viaje.candado], ['Contenedor', viaje.contenedor],
                              ['Booking', viaje.booking], ['Buque', viaje.buque]].map(([k, v]) => (
                                <div key={k} className="col-6 md:col-4">
                                    <div className="text-xs text-color-secondary uppercase">{k}</div>
                                    <div className="font-medium"><Codigo valor={v} /></div>
                                </div>
                            ))}
                            <div className="col-6 md:col-4">
                                <div className="text-xs text-color-secondary uppercase">Transportista</div>
                                <Transportista nombre={viaje.transportista} origen={viaje.transportistaOrigen} />
                            </div>
                        </div>
                        )}
                        <div className="flex align-items-center gap-2 flex-wrap mb-2">
                            {Object.entries(viaje.porTipo || {}).map(([t, n]) => (
                                <span key={t} className="flex align-items-center gap-1"><TagTipo tipo={t} /><b>{n}</b></span>
                            ))}
                            <span className="ml-auto"><EstadoRevision g={viaje} /></span>
                        </div>
                        {cargandoViaje && <div className="text-color-secondary">Cargando…</div>}
                        {seguridad && seg && (
                            <div className="mb-3">
                                <div className="flex align-items-center gap-2 flex-wrap mb-2">
                                    <Semaforo nivel={seg.nivel} />
                                    <span className="text-sm text-color-secondary">
                                        {seg.alertas} {seg.alertas === 1 ? 'alerta' : 'alertas'} · {seg.operaciones} de operación
                                    </span>
                                    <label htmlFor="ocultarOperacion" className="ml-auto text-sm flex align-items-center gap-2 cursor-pointer">
                                        <input type="checkbox" id="ocultarOperacion" checked={ocultarOperacion}
                                               onChange={(e) => setOcultarOperacion(e.target.checked)} />
                                        Ocultar operación
                                    </label>
                                </div>
                                {(seg.motivos || []).map((m) => (
                                    <div key={m} className="text-sm mb-1" style={{ color: nivelDe(seg.nivel).texto }}>{m}</div>
                                ))}
                                <div className="mb-2"><CicloContenedor ciclo={seg.ciclo} /></div>
                                <MapaViajeAlertas recorrido={seg.recorrido} eventos={seg.eventos} grupos={seg.grupos}
                                                  radioParadaM={Number(parametros?.parada_radio_m) || 150}
                                                  ocultarOperacion={ocultarOperacion}
                                                  onVerDetalle={abrirDetalle} />
                            </div>
                        )}
                        <div className="flex flex-column gap-1">
                            {alertasViaje.filter((a) => !(seguridad && ocultarOperacion && a.clase === 'OPERACION')).map((a, i, lista) => (
                                <React.Fragment key={a.monitoreoDetId}>
                                {seguridad && i === 0 && fueraDelRango(a) && (
                                    <div className="text-xs uppercase text-color-secondary mt-1 mb-1">
                                        {filtros.horas ? `Antes de las últimas ${filtros.horas} h` : `Antes del ${filtros.desde || 'rango'}`} · el viaje se muestra completo
                                    </div>
                                )}
                                {seguridad && !fueraDelRango(a) && (i === 0 || fueraDelRango(lista[i - 1])) && (
                                    <div className="text-xs uppercase font-semibold mt-2 mb-1" style={{ color: 'var(--primary-color)' }}>
                                        {rangoTexto}
                                    </div>
                                )}
                                <button type="button" onClick={() => abrirDetalle(a)}
                                        className="w-full text-left p-2 border-1 border-round surface-card cursor-pointer flex align-items-center gap-2 flex-wrap"
                                        style={{ borderColor: 'var(--surface-border)',
                                                 opacity: seguridad && a.clase === 'OPERACION' ? 0.6 : 1,
                                                 borderLeft: `3px solid ${seguridad ? colorEvento(a)
                                                     : esCritico(a.novedad) ? 'var(--red-500)' : 'var(--surface-border)'}` }}>
                                    {seguridad && (
                                        <span className="text-sm font-semibold text-center"
                                              style={{ minWidth: '1.6rem' }}>{a.orden}</span>
                                    )}
                                    <span className="text-sm text-color-secondary" style={{ minWidth: '6.5rem' }}>
                                        {cuando(a.fechaEvento)}
                                    </span>
                                    <TagTipo tipo={a.tipoEvento} />
                                    <span className="flex-1">
                                        <TagEvento evento={a.novedad || a.evento} />
                                        {seguridad && a.nota && (
                                            <span className="block text-xs text-color-secondary mt-1">{a.nota}</span>
                                        )}
                                    </span>
                                    {seguridad && a.tramoNombre && (
                                        <Tag value={a.tramoNombre} style={{ background: a.tramo === 'LLENO' ? '#185FA5' : '#7F77DD' }} />
                                    )}
                                    {seguridad && a.clase === 'OPERACION' && <Tag severity="secondary" value="operación" />}
                                    {!seguridad && fueraDelRango(a) && <Tag severity="warning" value="fuera del rango" />}
                                    <i className="pi pi-angle-right text-color-secondary" />
                                </button>
                                </React.Fragment>
                            ))}
                        </div>
                        {!seguridad && !cargandoViaje && alertasViaje.length === 100 && (
                            <small className="text-color-secondary">Se muestran las 100 más recientes; la revisión cubre las {(viaje.monitoreoDetIds || []).length}.</small>
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog visible={!!detalle} onHide={() => setDetalle(null)} header="Detalle de la alerta"
                    style={{ width: '44rem', maxWidth: '96vw' }} dismissableMask>
                {detalle && (
                    <div>
                        {/* Encabezado: lo que define la alerta, sin buscarlo en una lista. */}
                        <div className="flex align-items-center gap-2 flex-wrap pb-3 mb-3 border-bottom-1"
                             style={{ borderColor: 'var(--surface-border)' }}>
                            <TagTipo tipo={detalle.tipoEvento} />
                            <TagEvento evento={detalle.novedad || detalle.evento} />
                            <TagEstado estado={detalle.estadoAlerta} />
                            <span className="ml-auto text-sm text-color-secondary text-right">
                                <div>Evento: {fecha(detalle.fechaEvento)}</div>
                                {detalle.fechaIngreso && (
                                    <div style={{ fontSize: '.85em' }}>
                                        Ingresó: {fecha(detalle.fechaIngreso)}
                                    </div>
                                )}
                            </span>
                        </div>

                        <div className="grid">
                            {detalle.vip ? (
                                <div className="col-12">
                                    <div className="text-xs uppercase text-color-secondary mb-2">Pánico VIP</div>
                                    <DatosVip vip={detalle.vip} />
                                </div>
                            ) : (<>
                            <div className="col-12 md:col-7">
                                <div className="text-xs uppercase text-color-secondary mb-2">Unidad</div>
                                <div className="grid">
                                    {[['Placa', detalle.placa], ['Candado', detalle.dispositivo],
                                      ['Booking', detalle.booking], ['Contenedor', detalle.contenedor],
                                      ['Buque', detalle.buque]]
                                        .map(([k, v]) => (
                                            <React.Fragment key={k}>
                                                <div className="col-5 text-color-secondary">{k}</div>
                                                <div className="col-7"><Codigo valor={v} /></div>
                                            </React.Fragment>
                                        ))}
                                </div>
                            </div>

                            <div className="col-12 md:col-5">
                                <div className="text-xs uppercase text-color-secondary mb-2">Transporte</div>
                                <div className="grid">
                                    <div className="col-5 text-color-secondary">Transportista</div>
                                    <div className="col-7">
                                        <Transportista nombre={detalle.transportista}
                                                       origen={detalle.transportistaOrigen} />
                                    </div>
                                    <div className="col-5 text-color-secondary">Chofer</div>
                                    <div className="col-7">{detalle.chofer || '—'}</div>
                                </div>
                            </div>

                            {detalle.transportistaOrigen && detalle.transportistaOrigen !== 'BITACORA' && (
                                <div className="col-12">
                                    <small className="text-color-secondary">
                                        El transportista sale de {detalle.transportistaOrigen === 'VIAJE'
                                            ? 'la empresa del viaje' : 'ningún origen'}, no de la bitácora.
                                    </small>
                                </div>
                            )}
                            </>)}

                            <div className="col-12 mt-2">
                                <div className="text-xs uppercase text-color-secondary mb-2">
                                    Dónde ocurrió
                                </div>
                                <MapaAlerta lat={detalle.latitud} lng={detalle.longitud}
                                            puntoCercano={detalle.puntoCercano}
                                            titulo={detalle.vip ? detalle.vip.dispositivo : detalle.transportista}
                                            evento={detalle.novedad} />
                            </div>

                            <div className="col-12 mt-2">
                                <div className="text-xs uppercase text-color-secondary mb-2">
                                    Respuesta del operador
                                </div>
                                <div className="p-3 border-round surface-100 border-left-3"
                                     style={{ borderColor: 'var(--primary-color)' }}>
                                    {detalle.novedad && (
                                        <div className="mb-2"><TagEvento evento={detalle.novedad} /></div>
                                    )}
                                    {detalle.observacion || 'El operador no dejó texto libre.'}
                                </div>
                            </div>

                            {/* Tu lectura: la evidencia de auditoria de este usuario. */}
                            <div className="col-12 mt-2">
                                <div className="text-xs uppercase text-color-secondary mb-2">Tu lectura</div>
                                <div className="p-3 border-round surface-100 text-sm">
                                    {detalle.vistas
                                        ? <div>
                                            Abierta {detalle.vistas === 1 ? '1 vez' : `${detalle.vistas} veces`}
                                            {detalle.ultimaVista ? ` · última: ${fecha(detalle.ultimaVista)}` : ''}
                                          </div>
                                        : <div className="text-color-secondary">
                                            {detalle.lecturaLista ? 'No se pudo confirmar el registro de esta apertura.' : 'Registrando…'}
                                          </div>}
                                    {detalle.fechaLeida && (
                                        <div className="mt-1">Leída por primera vez: {fecha(detalle.fechaLeida)}</div>
                                    )}
                                    <HistorialAperturas key={`${detalle.monitoreoDetId}-${detalle.vistas}`}
                                                        monitoreoDetId={detalle.monitoreoDetId}
                                                        vistas={detalle.vistas} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default ConsultaAlertas;
